/**
 * «خرید اشتراک» on the landing sites — carried through sign-up and onboarding.
 *
 * parsinabot.ir and parsinadent.ir send a plan card's button to
 * `/subscription?plan=<kind>`. For a clinic owner that already works on its own:
 * auth.js keeps the path across the Keycloak round trip, and `/subscription`
 * forwards into «کلینیک من → اشتراک» with the query intact.
 *
 * A person with NO clinic is the case this file exists for. Every path they
 * open is sent to «شروع», and onboarding used to end on the new clinic's home —
 * so someone who pressed «خرید» signed up, built a clinic, and landed on پذیرش
 * with no idea where the plan they chose had gone. The no-clinic redirect
 * (StartRedirect) writes the intent here, and the two places onboarding ends
 * (the wizard's `enter`, quick setup's `enter`) read it back through
 * `clinicEntryPath`.
 *
 * What it never does is start a checkout. The billing page marks the card and
 * waits for the person to press it: the price — the congress price included —
 * is something they confirm, not something that happens to them after sign-up.
 *
 * `sessionStorage`, like the return path in auth.js: scoped to this tab, which
 * is the tab the sign-up happens in. The TTL stops a buy click that was
 * abandoned at «شروع» from steering a clinic created hours later in that tab.
 */

const KEY = 'purchase.intent.v1';
const TTL_MS = 2 * 60 * 60 * 1000;

/** The card kinds on the billing page (PlanOffers). Codes differ per catalogue; kinds do not. */
export const PLAN_KINDS = ['annual', 'perpetual', 'monthly'];

/** The `?plan=` a URL names, or '' for none or an unknown value. */
export function planKindFrom(search) {
  const kind = new URLSearchParams(search || '').get('plan') || '';
  return PLAN_KINDS.includes(kind) ? kind : '';
}

/** Remember that this person came to buy — with the card they chose, when there was one. */
export function stashPurchaseIntent(plan, now = Date.now()) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({
      plan: PLAN_KINDS.includes(plan) ? plan : '',
      at: now,
    }));
  } catch { /* a lost intent costs one click on «اشتراک», never the sign-up */ }
}

/** The intent, consumed: `{ plan }` once, then null. Null when stale or malformed. */
export function takePurchaseIntent(now = Date.now()) {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    const intent = raw ? JSON.parse(raw) : null;
    if (!intent || typeof intent.at !== 'number' || now - intent.at > TTL_MS) return null;
    return { plan: PLAN_KINDS.includes(intent.plan) ? intent.plan : '' };
  } catch {
    return null;
  }
}

/**
 * Where onboarding enters the clinic it just built: «اشتراک» when this person
 * came to buy, the clinic's home otherwise. Consumes the intent.
 */
export function clinicEntryPath(ref) {
  const intent = takePurchaseIntent();
  if (!intent) return `/c/${ref}/`;
  return intent.plan
    ? `/c/${ref}/clinic?view=billing&plan=${intent.plan}`
    : `/c/${ref}/clinic?view=billing`;
}
